import type { TFunction } from 'i18next';
import { JSX } from 'react';
import { Area } from 'recharts';
import type { Component } from '../../../api/pages-client/pages-client.type';
import type { TransformedTarget, TransformedTargetThreshold } from '../../../api/targets-client/targets-client.type';
import { TargetComparison } from '../../../api/targets-client/targets-client.type';
import { purpleDarkest, secondaryBase } from '../../../styles/design-tokens';
import { getTargetFill, getTargetStrokeDashArray } from '../../adherence/targets/targets.helpers';
import { getTargetThresholdLabels, TargetThreshold } from '../../adherence/targets/targets.type';
import { getComponentDateRange } from '../dashboards.helpers';
import { COLORS } from '../dashboards.type';

/**
 * Generates reference lines and areas for target visualization in recharts charts
 * @param {TransformedTarget} target - The transformed target object containing comparison type and time allocations
 * @returns {JSX.Element | null} JSX elements for reference lines/areas or null if invalid target values
 */
const getTargetAreaChart = (target: TransformedTarget, t: TFunction): JSX.Element => {
  const targetFillColor = getTargetFill(target.target_comparison, secondaryBase);
  const targetStrokeDashArray = getTargetStrokeDashArray(target.target_comparison);
  const targetBase =
    target.target_comparison === TargetComparison.GTE || target.target_comparison === TargetComparison.GT
      ? 'dataMax'
      : 'dataMin';
  return (
    <Area
      key={`target-area-${target.id}`}
      type="monotone"
      dataKey={`target_${target.id}`}
      name={`${target.name} (${t('common.target')})`}
      stroke={secondaryBase}
      strokeDasharray={targetStrokeDashArray}
      fill={targetFillColor}
      dot={false}
      baseValue={targetBase}
    />
  );
};

/**
 * Calculates the average and total values for a target, considering the target comparison type
 * @param {TransformedTarget} target - The transformed target object containing comparison type and time allocations
 * @returns {Object} An object with average_lower, average_upper, total_lower, and total_upper values
 */
const getProratedAverageTotals = (
  target: TransformedTarget,
): { average_lower: number; average_upper?: number; total_lower: number; total_upper?: number } => {
  if (
    target.target_comparison === TargetComparison.RANGE_EX ||
    target.target_comparison === TargetComparison.RANGE_IN
  ) {
    const values = Object.values(target.time_allocations);
    const lower = values.map((value) =>
      typeof value === 'object' && value !== null && 'value_lower' in value ? value.value_lower : 0,
    );
    const upper = values.map((value) =>
      typeof value === 'object' && value !== null && 'value_upper' in value ? value.value_upper : 0,
    );
    const totalLower = lower.reduce((acc, curr) => acc + curr, 0);
    const totalUpper = upper.reduce((acc, curr) => acc + curr, 0);
    const averageLower = lower.length ? Number((totalLower / lower.length).toFixed(2)) : 0;
    const averageUpper = upper.length ? Number((totalUpper / upper.length).toFixed(2)) : 0;
    return {
      average_lower: averageLower,
      average_upper: averageUpper,
      total_lower: Number(totalLower.toFixed(2)),
      total_upper: Number(totalUpper.toFixed(2)),
    };
  }
  const values = Object.values(target.time_allocations).map((value) => (typeof value === 'number' ? value : 0));
  const total = Number(values.reduce((acc, curr) => acc + curr, 0).toFixed(2));
  const average = values.length ? Number((total / values.length).toFixed(2)) : 0;
  return {
    average_lower: average,
    total_lower: total,
  };
};

/**
 * Generates a link to the detailed view for a dashboard component
 * @param {Component} component - The dashboard component to generate a link for
 * @param {string | undefined} portfolioId - The portfolio ID
 * @param {string | null} selectedWorkPeriodId - The selected work period ID
 * @returns {string | null} The URL to the component's detailed view, or null if no link can be generated
 *
 * This function creates navigation links based on the component's object type:
 * - For initiatives: Links to the strategy view
 * - For portfolios, projects, and boards: Links to process analysis
 * - For sprints: Links to the work periods module
 * - The link includes date range parameters from the component's configuration
 */
const getComponentDetailsLink = (
  component: Component,
  portfolioId: string | undefined,
  selectedWorkPeriodId?: string | null,
): string | null => {
  const componentData = component.component_data?.[0];
  if (!componentData) return null;
  const { startDate, endDate } = getComponentDateRange(componentData);
  const { object_type, object_ids, measure } = componentData;

  const selectedMeasuresParam = measure ? `&selectedMeasures=${encodeURIComponent(measure)}` : '';
  const baseProcessAnalysisLink = `/application/process-analysis?startDate=${startDate}&endDate=${endDate}&showSprintComparison=false${selectedMeasuresParam}`;

  switch (object_type) {
    case 'initiative':
      if (portfolioId && object_ids.length > 0) {
        return `/application/strategy/${portfolioId}/${object_ids[0]}`;
      }
      return null;
    case 'portfolio':
      return `${baseProcessAnalysisLink}&portfolioIds=${object_ids.join(',')}&activeTab=portfolios`;
    case 'project':
      return `${baseProcessAnalysisLink}&teamIds=${object_ids.join(',')}&activeTab=teams`;
    case 'board':
      return `${baseProcessAnalysisLink}&boardIds=${object_ids.join(',')}&activeTab=boards`;
    case 'sprint':
      if (object_ids.length > 0) {
        const selectedWorkPeriodParam = selectedWorkPeriodId
          ? `&selectedWorkPeriodId=${encodeURIComponent(selectedWorkPeriodId)}`
          : '';
        return `/application/work-periods?workPeriodBoardId=${object_ids[0]}&startDate=${startDate}&endDate=${endDate}${selectedWorkPeriodParam}`;
      }
      return null;
    default:
      return null;
  }
};

/**
 * Returns the default color for a given object based on its index
 * @param {string} objectId - The ID of the object
 * @param {Record<string, string>} colorMap - The color map for the object
 * @param {number} [index] - The index of the object
 * @returns {string} The default color for the object
 */
const getDefaultObjectColor = (
  objectId: string,
  colorMap: Record<string, string>,
  index?: number,
  measureColors?: string,
): string => {
  const colorKeys = Object.keys(COLORS);

  // If measureColors is provided (for multi-measure widgets), parse and use the color at the index
  if (measureColors && index !== undefined) {
    const colors = measureColors
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c);
    if (colors[index]) {
      return colors[index];
    }
  }

  if (index !== undefined) {
    return colorMap[objectId] || COLORS[colorKeys[index % colorKeys.length] as keyof typeof COLORS].stroke;
  }
  return colorMap[objectId] || purpleDarkest;
};

/**
 * Generates a human-readable message describing a target threshold for a specific date
 *
 * This function creates descriptive text for target thresholds (green, yellow, red) by:
 * - Retrieving the threshold bounds (lower/upper) for the specified date
 * - Formatting the message based on whether both bounds exist, or just one
 * - Using localized labels for threshold colors and terminology
 *
 * @param {TransformedTarget} target - The target object containing threshold data
 * @param {TargetThreshold} targetThreshold - The threshold type (green, yellow, or red)
 * @param {string} dateKey - The date key to look up threshold values for
 * @returns {string} A formatted message describing the threshold range or bound
 *
 * @example
 * // Returns: "Within Green threshold: 80 - 100"
 * getTargetThresholdMessage(target, TargetThreshold.GREEN, '2024-01-01')
 *
 * @example
 * // Returns: "Above Yellow threshold: 50"
 * getTargetThresholdMessage(target, TargetThreshold.YELLOW, '2024-01-01')
 */

const getTargetThresholdMessage = (
  transformedTargetThreshold: TransformedTargetThreshold,
  targetThreshold: TargetThreshold,
  t: TFunction,
): string => {
  const lower = transformedTargetThreshold?.[`${targetThreshold}_threshold_lower`];
  const upper = transformedTargetThreshold?.[`${targetThreshold}_threshold_upper`];
  const labels = getTargetThresholdLabels(t);
  // Explicit presence checks: lower/upper are `number | null | undefined`, so
  // a truthy test would silently drop a valid 0 threshold and render the
  // wrong message (or none at all).
  const hasLower = lower != null;
  const hasUpper = upper != null;
  return hasLower && hasUpper
    ? `Within ${labels[targetThreshold]} ${t('targets.threshold')}: ${lower} - ${upper}`
    : hasLower
      ? `Above ${labels[targetThreshold]} ${t('targets.threshold')}: ${lower} `
      : hasUpper
        ? `Below ${labels[targetThreshold]} ${t('targets.threshold')}: ${upper}`
        : '';
};

/**
 * Calculates and adds trend lines to chart data using least squares regression
 * @param {Array} data - The chart data array
 * @param {boolean} isSprintData - Whether this is sprint data (uses last value per sprint)
 * @returns {Array} The data with trend line values added
 */
const addTrendLinesToData = (data: Record<string, any>[], isSprintData = false): Record<string, any>[] => {
  if (!data || data.length <= 1) {
    return data;
  }

  // Clone the data to avoid modifying the original
  const result = JSON.parse(JSON.stringify(data)) as Record<string, any>[];

  // Collect all numeric keys across the entire dataset (not just the first row)
  // This ensures series that start later in the timeline still get trend lines
  const keySet = new Set<string>();
  data.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (
        key !== 'date' &&
        !key.endsWith('_trend') &&
        !key.endsWith('Name') &&
        !key.startsWith('target_') &&
        typeof row[key] === 'number'
      ) {
        keySet.add(key);
      }
    });
  });
  const keys = Array.from(keySet);

  // For sprint data, group by sprint and take only the last value from each sprint
  let dataPointsForRegression = data;
  let xValues: number[] = [];

  if (isSprintData) {
    // Group data points by sprint (identified by the 'date' field being the same)
    // For sprint widgets, consecutive points with different dates are different sprints
    const sprintGroups: Record<string, number[]> = {};
    data.forEach((point, index) => {
      const sprintKey = point.date;
      if (!sprintGroups[sprintKey]) {
        sprintGroups[sprintKey] = [];
      }
      sprintGroups[sprintKey].push(index);
    });

    // Get the last index from each sprint group
    const lastIndicesPerSprint = Object.values(sprintGroups).map((indices) => indices[indices.length - 1]);

    // Use only these last data points for regression calculation
    dataPointsForRegression = lastIndicesPerSprint.map((idx) => data[idx]);

    // Create x values as sequential indices for the sprint end points
    xValues = Array.from({ length: dataPointsForRegression.length }, (_, i) => i);
  } else {
    // Use sequential indices for regression calculation
    xValues = Array.from({ length: data.length }, (_, i) => i);
  }

  // For each numeric key, calculate and add the trend line
  keys.forEach((key) => {
    // Get y values for current key (from the sprint end points only if sprint data)
    const yValuesForRegression = dataPointsForRegression.map((point) => Number(point[key] || 0));

    // Calculate mean of x and y values
    const xMean = xValues.length > 0 ? xValues.reduce((sum, x) => sum + x, 0) / xValues.length : 0;
    const yMean =
      yValuesForRegression.length > 0
        ? yValuesForRegression.reduce((sum, y) => sum + y, 0) / yValuesForRegression.length
        : 0;

    // Calculate slope (m) and y-intercept (b) using least squares method
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < dataPointsForRegression.length; i++) {
      const xDiff = xValues[i] - xMean;
      const yDiff = yValuesForRegression[i] - yMean;
      numerator += xDiff * yDiff;
      denominator += xDiff * xDiff;
    }

    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;

    // Generate trend line values for ALL data points (not just sprint endpoints)
    const trendLineKey = `${key}_trend`;
    if (isSprintData) {
      // For sprint data, map each original data point to its position in the sprint sequence
      const sprintGroups: Record<string, number[]> = {};
      data.forEach((point, index) => {
        const sprintKey = point.date;
        if (!sprintGroups[sprintKey]) {
          sprintGroups[sprintKey] = [];
        }
        sprintGroups[sprintKey].push(index);
      });

      const sprintNames = Object.keys(sprintGroups);
      let sprintIndex = 0;

      data.forEach((point, dataIndex) => {
        const currentSprintName = point.date;
        sprintIndex = sprintNames.indexOf(currentSprintName);

        const sprintIndices = sprintGroups[currentSprintName];
        const positionInSprint = sprintIndices.indexOf(dataIndex);
        const sprintLength = sprintIndices.length;

        // Interpolate within the sprint based on position
        const xInterpolated = sprintLength !== 0 ? sprintIndex + positionInSprint / sprintLength : sprintIndex;

        result[dataIndex][trendLineKey] = Math.max(0, Math.round(slope * xInterpolated + intercept));
      });
    } else {
      // For non-sprint data, use sequential indices
      for (let i = 0; i < result.length; i++) {
        result[i][trendLineKey] = Math.max(0, Math.round(slope * i + intercept));
      }
    }
  });

  return result;
};

/**
 * Returns an abbreviated Y-axis label string from an array of measure unit strings.
 * Deduplicates units and joins different units with ' / '.
 *
 * @param {string[]} measureUnits - Array of unit strings from the available-measures API (e.g., 'Percentage', 'Tasks', 'Points')
 * @returns {string} Abbreviated label (e.g., '%', 'Ct', 'Pts', 'Ct / %')
 *
 * @example
 * getYAxisLabel(['Percentage', 'Percentage']) // Returns '%'
 * getYAxisLabel(['Tasks', 'Percentage']) // Returns 'Ct / %'
 * getYAxisLabel([]) // Returns ''
 */
const getYAxisLabel = (measureUnits: string[]): string => {
  const abbreviate = (unit: string): string => {
    switch (unit) {
      case 'Percentage':
        return '%';
      case 'Tasks':
      case 'Number':
        return 'Ct';
      case 'Points':
        return 'Pts';
      case 'Hours':
        return 'Hrs';
      case 'Days':
        return 'Days';
      default:
        return '';
    }
  };

  const abbreviated = [...new Set(measureUnits.map(abbreviate))].filter(Boolean);
  return abbreviated.join(' / ');
};

export {
  addTrendLinesToData,
  getComponentDetailsLink,
  getDefaultObjectColor,
  getProratedAverageTotals,
  getTargetAreaChart,
  getTargetThresholdMessage,
  getYAxisLabel,
};
